import { useState } from 'react';
import {
  FileBox,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Check,
  X,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Package,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useProducts } from '@/hooks/useProducts';
import { toast } from 'sonner';

interface GoodsRequest {
  id: string;
  organization_id: string;
  requesting_location_id: string;
  fulfilling_location_id: string | null;
  request_number: string;
  status: 'pending' | 'approved' | 'rejected' | 'fulfilled' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  requested_by: string | null;
  approved_by: string | null;
  notes: string | null;
  rejection_reason: string | null;
  created_at: string;
  requesting_location?: { id: string; name: string } | null;
  fulfilling_location?: { id: string; name: string } | null;
  requester?: { first_name: string | null; last_name: string | null } | null;
}

interface GoodsRequestItem {
  id: string;
  request_id: string;
  product_id: string;
  quantity_requested: number;
  quantity_approved: number | null;
  product?: { id: string; name: string; sku: string | null } | null;
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  fulfilled: { label: 'Fulfilled', color: 'bg-green-100 text-green-700', icon: Package },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: X },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-600' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-600' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-600' },
};

export default function GoodsRequests() {
  const { organization, user } = useAuth();
  const { locations } = useLocations();
  const { products } = useProducts();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<GoodsRequest | null>(null);
  const [selectedRequestItems, setSelectedRequestItems] = useState<GoodsRequestItem[]>([]);

  // Form state
  const [formData, setFormData] = useState<{
    requesting_location_id: string;
    fulfilling_location_id: string;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    notes: string;
  }>({
    requesting_location_id: '',
    fulfilling_location_id: '',
    priority: 'normal',
    notes: '',
  });
  const [formItems, setFormItems] = useState<{ product_id: string; quantity: string }[]>([
    { product_id: '', quantity: '' },
  ]);
  const [formError, setFormError] = useState<string | null>(null);

  // Approval state
  const [approvalQuantities, setApprovalQuantities] = useState<Record<string, string>>({});
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch goods requests
  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['goods-requests', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('goods_requests')
        .select(`
          *,
          requesting_location:locations!goods_requests_requesting_location_id_fkey(id, name),
          fulfilling_location:locations!goods_requests_fulfilling_location_id_fkey(id, name),
          requester:profiles!goods_requests_requested_by_fkey(first_name, last_name)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as GoodsRequest[];
    },
    enabled: !!organization?.id,
  });

  // Fetch request items
  const fetchRequestItems = async (requestId: string) => {
    const { data, error } = await supabase
      .from('goods_request_items')
      .select(`
        *,
        product:products(id, name, sku)
      `)
      .eq('request_id', requestId);
    if (error) throw error;
    return data as GoodsRequestItem[];
  };

  // Create request mutation
  const createRequest = useMutation({
    mutationFn: async () => {
      const itemsToInsert = formItems
        .filter(item => item.product_id || item.quantity)
        .map(item => ({
          request_id: '',
          product_id: item.product_id,
          quantity_requested: Number(item.quantity),
        }));

      if (itemsToInsert.length === 0 || itemsToInsert.some(item => !item.product_id || !Number.isFinite(item.quantity_requested) || item.quantity_requested <= 0)) {
        throw new Error('Each request line needs a product and a quantity greater than zero');
      }

      if (new Set(itemsToInsert.map(item => item.product_id)).size !== itemsToInsert.length) {
        throw new Error('A product can appear only once in a request');
      }

      const { data: requestId, error } = await supabase.rpc('create_goods_request', {
        p_requesting_location_id: formData.requesting_location_id,
        p_fulfilling_location_id: formData.fulfilling_location_id || null,
        p_priority: formData.priority,
        p_notes: formData.notes || null,
        p_items: itemsToInsert.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity_requested,
        })),
      });

      if (error) throw error;
      return requestId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-requests'] });
      toast.success('Request created', { description: 'Your goods request has been submitted' });
      setCreateDialogOpen(false);
      resetForm();
    },
  });

  // Approve request mutation
  const approveRequest = useMutation({
    mutationFn: async ({ requestId, approved }: { requestId: string; approved: boolean }) => {
      if (approved) {
        const items = selectedRequestItems.map(item => ({
          item_id: item.id,
          quantity_approved: parseFloat(approvalQuantities[item.id] || '0'),
        }));

        if (items.some((item, index) =>
          !Number.isFinite(item.quantity_approved) ||
          item.quantity_approved < 0 ||
          item.quantity_approved > selectedRequestItems[index].quantity_requested
        )) {
          throw new Error('Approved quantity must be between zero and the requested quantity');
        }

        const { error } = await supabase.rpc('approve_goods_request', {
          p_request_id: requestId,
          p_items: items,
        });
        if (error) throw error;
        return;
      }

      // Update request status
      const { error } = await supabase
        .from('goods_requests')
        .update({
          status: approved ? 'approved' : 'rejected',
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
          rejection_reason: !approved ? rejectionReason : null,
        })
        .eq('id', requestId);

      if (error) throw error;
    },
    onSuccess: (_, { approved }) => {
      queryClient.invalidateQueries({ queryKey: ['goods-requests'] });
      toast.success(approved ? 'Request approved' : 'Request rejected', {
        description: approved 
          ? 'The goods request has been approved. A transfer can now be created.'
          : 'The goods request has been rejected.',
      });
      setApproveDialogOpen(false);
    },
  });

  // Cancel request mutation
  const cancelRequest = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from('goods_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-requests'] });
      toast.success('Request cancelled');
    },
  });

  // Filter requests
  const filteredRequests = requests.filter(request => {
    if (statusFilter !== 'all' && request.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        request.request_number.toLowerCase().includes(query) ||
        request.requesting_location?.name.toLowerCase().includes(query) ||
        request.fulfilling_location?.name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.requesting_location_id) {
      setFormError('Please select a requesting location');
      return;
    }

    if (formData.fulfilling_location_id === formData.requesting_location_id) {
      setFormError('Requesting and fulfilling locations must be different');
      return;
    }

    const populatedItems = formItems.filter(item => item.product_id || item.quantity);
    if (populatedItems.length === 0) {
      setFormError('Please add at least one item');
      return;
    }

    if (populatedItems.some(item => !item.product_id || !Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0)) {
      setFormError('Each request line needs a product and a quantity greater than zero');
      return;
    }

    if (new Set(populatedItems.map(item => item.product_id)).size !== populatedItems.length) {
      setFormError('A product can appear only once in a request');
      return;
    }

    await createRequest.mutateAsync();
  };

  const openViewDialog = async (request: GoodsRequest) => {
    setSelectedRequest(request);
    const items = await fetchRequestItems(request.id);
    setSelectedRequestItems(items);
    setViewDialogOpen(true);
  };

  const openApproveDialog = async (request: GoodsRequest) => {
    setSelectedRequest(request);
    const items = await fetchRequestItems(request.id);
    setSelectedRequestItems(items);
    // Initialize with requested quantities
    const initialQuantities: Record<string, string> = {};
    items.forEach(item => {
      initialQuantities[item.id] = item.quantity_requested.toString();
    });
    setApprovalQuantities(initialQuantities);
    setRejectionReason('');
    setApproveDialogOpen(true);
  };

  const addFormItem = () => {
    setFormItems([...formItems, { product_id: '', quantity: '' }]);
  };

  const removeFormItem = (index: number) => {
    setFormItems(formItems.filter((_, i) => i !== index));
  };

  const updateFormItem = (index: number, field: string, value: string) => {
    const newItems = [...formItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormItems(newItems);
  };

  const resetForm = () => {
    setFormData({
      requesting_location_id: '',
      fulfilling_location_id: '',
      priority: 'normal',
      notes: '',
    });
    setFormItems([{ product_id: '', quantity: '' }]);
    setFormError(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Goods Requests</h2>
          <p className="text-sm text-muted-foreground">
            Request inventory transfers between locations
          </p>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          New Request
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Request # or location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="fulfilled">Fulfilled</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Requests Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading requests...</span>
          </div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileBox className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No requests found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create a request to transfer goods between locations
              </p>
              <Button variant="blaze" className="gap-2" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                New Request
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Request #
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  From
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  To
                </th>
                <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                  Priority
                </th>
                <th className="text-center text-sm font-medium text-muted-foreground px-4 py-3">
                  Status
                </th>
                <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                  Date
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredRequests.map((request) => {
                const statusConfig = STATUS_CONFIG[request.status];
                const priorityConfig = PRIORITY_CONFIG[request.priority];
                const StatusIcon = statusConfig.icon;
                return (
                  <tr key={request.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openViewDialog(request)}
                        className="font-medium text-sm hover:underline"
                      >
                        {request.request_number}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {request.fulfilling_location?.name || 
                        <span className="text-muted-foreground">Any location</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {request.requesting_location?.name}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={cn('text-xs', priorityConfig.color)}>
                        {priorityConfig.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={cn('gap-1', statusConfig.color)}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(request.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(request)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {request.status === 'pending' && (
                            <>
                              <DropdownMenuItem onClick={() => openApproveDialog(request)}>
                                <Check className="h-4 w-4 mr-2" />
                                Approve/Reject
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  if (confirm('Cancel this request?')) {
                                    cancelRequest.mutateAsync(request.id);
                                  }
                                }}
                                className="text-red-600"
                              >
                                <X className="h-4 w-4 mr-2" />
                                Cancel
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Request Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <form onSubmit={handleCreateRequest}>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>New Goods Request</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Request inventory from another location
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody className="max-h-[60vh] overflow-y-auto">
              <div className="grid gap-4">
                {formError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Request For (Your Location) *</Label>
                    <Select
                      value={formData.requesting_location_id}
                      onValueChange={(value) => setFormData({ ...formData, requesting_location_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Request From</Label>
                    <Select
                      value={formData.fulfilling_location_id || '__any__'}
                      onValueChange={(value) => setFormData({ ...formData, fulfilling_location_id: value === '__any__' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__any__">Any location</SelectItem>
                        {locations
                          .filter(l => l.id !== formData.requesting_location_id)
                          .map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => 
                      setFormData({ ...formData, priority: value as 'low' | 'normal' | 'high' | 'urgent' })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Requested Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addFormItem}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Item
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {formItems.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 rounded-lg border">
                        <div className="flex-1">
                          <Select
                            value={item.product_id}
                            onValueChange={(value) => updateFormItem(index, 'product_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select product" />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-24">
                          <Input
                            type="number"
                            min="1"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateFormItem(index, 'quantity', e.target.value)}
                          />
                        </div>
                        {formItems.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeFormItem(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Reason for request..."
                    rows={2}
                  />
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="blaze" disabled={createRequest.isPending}>
                {createRequest.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Submit Request
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* View Request Dialog */}
      <ResponsiveModal open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              Request {selectedRequest?.request_number}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {selectedRequest?.requesting_location?.name} 
              {selectedRequest?.fulfilling_location && ` ← ${selectedRequest.fulfilling_location.name}`}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-4">
                {selectedRequest && (
                  <>
                    <Badge className={cn('gap-1', STATUS_CONFIG[selectedRequest.status].color)}>
                      {STATUS_CONFIG[selectedRequest.status].label}
                    </Badge>
                    <Badge className={cn(PRIORITY_CONFIG[selectedRequest.priority].color)}>
                      {PRIORITY_CONFIG[selectedRequest.priority].label} priority
                    </Badge>
                  </>
                )}
              </div>

              {/* Items */}
              <div className="rounded-lg border">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left text-sm font-medium text-muted-foreground px-4 py-2">
                        Product
                      </th>
                      <th className="text-right text-sm font-medium text-muted-foreground px-4 py-2">
                        Requested
                      </th>
                      <th className="text-right text-sm font-medium text-muted-foreground px-4 py-2">
                        Approved
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedRequestItems.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-2">
                          <p className="font-medium text-sm">{item.product?.name}</p>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {item.quantity_requested}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {item.quantity_approved ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedRequest?.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm mt-1">{selectedRequest.notes}</p>
                </div>
              )}

              {selectedRequest?.rejection_reason && (
                <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  <Label className="text-red-600">Rejection Reason</Label>
                  <p className="text-sm mt-1">{selectedRequest.rejection_reason}</p>
                </div>
              )}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Approve/Reject Dialog */}
      <ResponsiveModal open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Review Request</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Approve or reject {selectedRequest?.request_number}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Approval quantities */}
              <div className="space-y-2">
                <Label>Approve Quantities</Label>
                {selectedRequestItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 p-3 rounded-lg border">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.product?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Requested: {item.quantity_requested}
                      </p>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        min="0"
                        max={item.quantity_requested}
                        value={approvalQuantities[item.id] || ''}
                        onChange={(e) => setApprovalQuantities({
                          ...approvalQuantities,
                          [item.id]: e.target.value,
                        })}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Rejection reason */}
              <div className="space-y-2">
                <Label>Rejection Reason (if rejecting)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  rows={2}
                />
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedRequest && approveRequest.mutateAsync({ 
                requestId: selectedRequest.id, 
                approved: false 
              })}
              disabled={approveRequest.isPending || !rejectionReason}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              variant="blaze"
              onClick={() => selectedRequest && approveRequest.mutateAsync({ 
                requestId: selectedRequest.id, 
                approved: true 
              })}
              disabled={approveRequest.isPending}
            >
              {approveRequest.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
